import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardPieChart, {
  type DashboardPieChartSlice,
} from '@components/pages/Dashboard/MetricCharts/DashboardPieChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import { DashboardMetricTileGrid } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'

const OPERATIONS_PIE_SIZE = 230

type OperationsDonutCardProps = {
  title: string
  titleHref: string
  metrics: DashboardMetricItemData[]
  chartData: DashboardPieChartSlice[]
  loading?: boolean
  /** Response has arrived (`data != null`); drives the pending skeleton. */
  hasPayload: boolean
}

export function OperationsDonutCard({
  title,
  titleHref,
  metrics,
  chartData,
  loading = false,
  hasPayload,
}: OperationsDonutCardProps): React.ReactElement {
  const hasMetricData = hasNonZeroDashboardValues(
    ...metrics.map((metric) => metric.value),
  )
  const hasChartData = hasNonZeroDashboardValues(
    ...chartData.map((slice) => slice.value),
  )
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasMetricData || hasChartData,
  )
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasChartData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title={title}
      titleHref={titleHref}
      className="dashboard-operations-donut-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-operations-donut-inner">
        <DashboardMetricTileGrid metrics={metrics} columns={4} />
        {isChartEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-operations-donut-chart-area">
            <DashboardPieChart
              data={chartData}
              size={OPERATIONS_PIE_SIZE}
              valueFormatter={(v) => `${v}%`}
              className="dashboard-operations-donut-pie-chart"
            />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
